import {showCascadeHeldTasksModal} from '../components/modals/CascadeHeldTasksModal';
import {confirmCascadeHeldTasks} from '../../../components/scheduling/scheduling_mutations';
import {dispatch, EVENT_ID} from '../../../containers/event_manager';
import {trackEvent} from '../../../tracking/amplitude/TrackingV2';

/**
 * The most times in a row the confirm prompt may re-raise itself.
 *
 * Each round releases at least one finished task and a chain is finite, so this
 * terminates on its own - but nothing in the code makes that a guarantee, and an
 * endless prompt is a bad way to discover otherwise. Five is well past any real
 * chain; reaching it means the server is reporting tasks it is not moving, and
 * stopping there still leaves a consistent chain.
 */
const MAX_CONFIRM_ROUNDS = 5;

/**
 * Raise the prompt, and raise it again if releasing these tasks walked the chain
 * into more completed work.
 *
 * Confirming does not always finish the job: a chain can hold a second finished
 * task deeper down, which only becomes reachable once the first one moves. Asking
 * once and stopping would leave the chain half-cascaded with nothing to say so.
 */
const promptFor = (heldTaskIds, round = 1) => {
	showCascadeHeldTasksModal({
		heldTaskIds,
		// The confirm goes out as a plain fetch rather than a Relay mutation, so
		// nothing in the store moves on its own, and the socket push that the
		// cascade raises deliberately skips the client that asked for it. Without
		// this the user answers "move them" and watches the completed tasks keep
		// their old dates until a reload.
		onConfirm: ids =>
			confirmCascadeHeldTasks({ids}, response => {
				dispatch(EVENT_ID.SCHEDULING_RELOAD_DATA);
				const stillHeld = response?.confirmCascadeHeldTasks?.stillHeldTasks;
				if (stillHeld && stillHeld.length > 0 && round < MAX_CONFIRM_ROUNDS) {
					promptFor(stillHeld.map(task => task.id).filter(Boolean), round + 1);
				}
			}),
	});
};

/**
 * Everything that has to happen in the browser after a write whose cascade ran.
 *
 * One function for all three write paths, because the three follow-ups have to
 * stay in step and they very nearly did not: the analytics event originally hung
 * off the Relay task-update mutation alone, which is the one path a timeline drag
 * never takes.
 *
 * What it does, and why each part is needed:
 *
 * - **Reload the canvases.** They keep their state in a DataManager rather than
 *   in the Relay store, so a store update never reaches them, and the cascade's
 *   socket push deliberately skips the client that caused it. Nothing else
 *   listens to SCHEDULING_RELOAD_DATA, so on every other page this is a no-op -
 *   which is correct, because Relay-backed views take the new dates straight off
 *   `cascadeMovedTasks`.
 * - **Report the cascade.** Cascade runs server-side, where Forecast sends no
 *   analytics, so without an event from here the feature is invisible to product
 *   analysis. The server writes its own audit row on every path regardless.
 * - **Offer to move completed tasks it declined to move.**
 *
 * <p>The event count and the audit rows will NOT agree, by design: cascade also
 * runs where there is no browser at all - the public API, inbound integrations,
 * imports - and those produce a row and no event. The event means "cascades a
 * person set off in the web app"; the database is the complete count. That is the
 * only discrepancy there should be, which is why every browser path calls this.
 *
 * @param payload the cascade-carrying part of a mutation response
 */
const handleCascade = payload => {
	if (!payload) {
		return;
	}
	const movedTasks = payload.cascadeMovedTasks || [];
	const heldTasks = payload.cascadeHeldTasks || [];

	if (movedTasks.length > 0) {
		dispatch(EVENT_ID.SCHEDULING_RELOAD_DATA);
		trackEvent('Dependency Cascade', 'Ran', {
			tasksMoved: movedTasks.length,
			completedTasksHeld: heldTasks.length,
		});
	}

	const heldTaskIds = heldTasks.map(task => task.id).filter(Boolean);
	if (heldTaskIds.length > 0) {
		promptFor(heldTaskIds);
	}
};

/**
 * Follow up on a task update - from the modal, a list, or a timeline bar drag.
 *
 * Safe to call on any update response: almost every one carries no cascade at all,
 * in which case this does nothing.
 *
 * @param response the updateTask mutation payload
 */
export const handleCascadeAfterTaskUpdate = response => handleCascade(response?.updateTask);

/**
 * Same, for a Timeline chain drag.
 *
 * The drag writes through a different mutation, so its cascade arrives on its own
 * payload rather than updateTask's.
 *
 * @param response the shiftDependencyChain mutation payload
 */
export const handleCascadeAfterChainShift = response => handleCascade(response?.shiftDependencyChain);
